import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { FileText, Download, Plus, Search, Filter, X, UploadCloud, AlertCircle } from 'lucide-react';
import { DOCUMENT_CATEGORIES, DocumentCategory, DocumentVisibility } from '@ftt/shared';

export const AdminDocumentsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [clientId, setClientId] = useState('');
  const [category, setCategory] = useState<DocumentCategory>('EDITORIAL');
  const [visibility, setVisibility] = useState<DocumentVisibility>('CLIENT_ONLY');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data: documentsRes, isLoading } = useQuery({
    queryKey: ['admin-documents'],
    queryFn: () => api.get('/admin/documents').then((res) => res.data.data),
  });

  const { data: clientsRes } = useQuery({
    queryKey: ['admin-clients'],
    queryFn: () => api.get('/admin/clients').then((res) => res.data.data),
  });

  const clients = clientsRes || [];
  const documents = documentsRes || [];

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('Please select a file.');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', title || uploadFile.name);
      formData.append('clientId', clientId || clients[0]?.id || 'clt-profile-01');
      formData.append('category', category);
      formData.append('visibility', visibility);
      return api.post('/admin/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-documents'] });
      setIsUploadOpen(false);
      setUploadFile(null);
      setTitle('');
      setErrorMsg(null);
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.error?.message || err.message || 'Upload failed.');
    },
  });

  const updateVisibilityMutation = useMutation({
    mutationFn: async ({ id, visibility }: { id: string; visibility: DocumentVisibility }) => {
      return api.put(`/admin/documents/${id}`, { visibility });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-documents'] });
    },
  });

  const filteredDocs = documents.filter((d: any) => {
    const q = searchTerm.toLowerCase();
    return (
      d.title.toLowerCase().includes(q) ||
      d.fileName.toLowerCase().includes(q) ||
      d.category.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-8">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white">
            Global Document Repository
          </h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Manage author contracts, proofs, manuscript versions, and access control permissions.
          </p>
        </div>

        <button
          onClick={() => setIsUploadOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs shadow-sm transition-colors self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Upload File to Client</span>
        </button>
      </div>

      {/* Filter / Search Bar */}
      <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search documents by title or file name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-brand-500"
          />
        </div>
        <span className="text-xs font-mono text-slate-400">
          Total: {documents.length} Files
        </span>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-400">Loading documents...</div>
      ) : filteredDocs.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-950 border border-slate-800 text-center space-y-3">
          <FileText className="w-12 h-12 text-slate-600 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-white">No Documents Found</h3>
        </div>
      ) : (
        <div className="bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-900/80 text-slate-400 font-mono text-[11px] uppercase border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4">File Name &amp; Title</th>
                  <th className="px-6 py-4">Client / Author</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4">Access Visibility</th>
                  <th className="px-6 py-4">Size &amp; Date</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 font-sans">
                {filteredDocs.map((doc: any) => (
                  <tr key={doc.id} className="hover:bg-slate-900/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-bold text-white">{doc.title}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{doc.fileName}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-300">
                      {doc.client ? `${doc.client.firstName} ${doc.client.lastName}` : 'System'}
                      {doc.project && <span className="block text-[10px] text-brand-400 font-mono">[{doc.project.projectCode}]</span>}
                    </td>
                    <td className="px-6 py-4 font-mono text-[10px] uppercase font-bold text-brand-300">
                      {doc.category}
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={doc.visibility}
                        onChange={(e) => updateVisibilityMutation.mutate({ id: doc.id, visibility: e.target.value as DocumentVisibility })}
                        className="bg-slate-900 border border-slate-700 text-[11px] font-mono rounded px-2 py-1 text-slate-200"
                      >
                        <option value="CLIENT_ONLY">Client Only</option>
                        <option value="ADMIN_ONLY">Admin / Staff Only</option>
                        <option value="PUBLIC">Public</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-slate-400 font-mono text-[11px]">
                      {Math.round(doc.fileSize / 1024)} KB &bull; {new Date(doc.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <a
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-white font-mono text-xs inline-flex items-center gap-1 border border-slate-700"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Download</span>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {isUploadOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-950 rounded-2xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-800">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-serif text-xl font-bold text-white">Upload File to Client Repository</h3>
              <button onClick={() => setIsUploadOpen(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {errorMsg && (
              <div className="p-3.5 rounded-lg bg-rose-950 border border-rose-800 text-rose-300 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                  Assign to Client / Author
                </label>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                >
                  {clients.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.user?.firstName} {c.user?.lastName} ({c.companyName || 'Independent'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                  Document Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Master Page Proof Vol 1"
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as DocumentCategory)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                  >
                    {DOCUMENT_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                    Visibility
                  </label>
                  <select
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value as DocumentVisibility)}
                    className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                  >
                    <option value="CLIENT_ONLY">Client Only</option>
                    <option value="ADMIN_ONLY">Staff / Admin Only</option>
                    <option value="PUBLIC">Public</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                  File Attachment *
                </label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="w-full text-xs text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => setIsUploadOpen(false)}
                className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-900"
              >
                Cancel
              </button>
              <button
                onClick={() => uploadMutation.mutate()}
                disabled={!uploadFile || uploadMutation.isPending}
                className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs disabled:opacity-50"
              >
                {uploadMutation.isPending ? 'Uploading...' : 'Upload File'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
